import type { CorpusFeed } from "@shared/types/corpus/api.ts";

/*
 * Every route path of the platform, declared once as a tree. A node's `pattern` is its full path,
 * with `:name` parameters as the route table in App.tsx matches it, and its `path(params)` fills
 * the parameters in, so a link is built from the same declaration as its route. Node names are the
 * tree's keys, and `path` and `pattern` are reserved: no route may be called either.
 */

/** The parameters a route pattern names, as `{ name: string }`. */
type PatternParams<P extends string> = P extends `${infer _Head}:${infer Name}/${infer Rest}`
  ? { [K in Name]: string } & PatternParams<`/${Rest}`>
  : P extends `${infer _Head}:${infer Name}`
    ? { [K in Name]: string }
    : Record<never, never>;

/** The arguments of `path()`: none when the pattern names no parameters. */
type PathArgs<P extends string> = keyof PatternParams<P> extends never ? [] : [PatternParams<P>];

/** A route: its full pattern, and the path that fills its parameters in. */
export interface RouteNode<P extends string> {
  readonly pattern: P;
  path(...args: PathArgs<P>): string;
}

/** The full pattern of a segment under a parent pattern. */
type Join<P extends string, S extends string> = P extends "" ? S : `${P}/${S}`;

/** The named children of a route, each itself a route. */
type Children = Record<string, { readonly pattern: string }>;

/** Creates a route under a parent pattern, with children created under it when given. */
type Factory<P extends string> = <S extends string, C extends Children = Record<never, never>>(
  segment: S,
  children?: (at: Factory<Join<P, S>>) => C
) => RouteNode<Join<P, S>> & C;

/** Fills a pattern's `:name` segments, encoding each value as one path segment. */
function fillPattern(pattern: string, params: Record<string, string>): string {
  return pattern.replace(/:(\w+)/g, (_match, name: string) => encodeURIComponent(params[name]));
}

/** A factory whose routes sit under `parent`. */
function makeFactory<P extends string>(parent: P): Factory<P> {
  const factory = (segment: string, children?: (at: Factory<string>) => Children) => {
    const pattern = parent === "" ? segment : `${parent}/${segment}`;
    const node = {
      pattern,
      path: (params: Record<string, string> = {}) => fillPattern(pattern, params),
    };
    return children ? Object.assign(node, children(makeFactory(pattern))) : node;
  };
  return factory as Factory<P>;
}

/** Builds the route tree from the root factory. */
function buildRoutes<C extends Children>(build: (at: Factory<"">) => C): C {
  return build(makeFactory(""));
}

/** Every route of the platform. */
export const ROUTES = buildRoutes((at) => ({
  landing: at("/"),
  fetching: at("/fetching"),
  review: at("/review"),
  outsideHealth: at("/outside-health"),
  analysing: at("/analysing/:claimId"),
  verify: at("/verify/:userEventVerificationId"),
  account: at("/account", (at) => ({
    profile: at("profile"),
    verifications: at("verifications"),
    feedback: at("feedback"),
  })),
  signIn: at("/sign-in"),
  joinBeta: at("/join-beta"),
  forgotPassword: at("/forgot-password"),
  resetPassword: at("/reset-password"),

  expertInsights: at("/expert-insights", (at) => ({
    insights: at("insights", (at) => ({
      new: at("new"),
      edit: at(":id/edit"),
    })),
  })),

  internal: at("/internal", (at) => ({
    home: at("home"),
    annotation: at("annotation", (at) => ({
      review: at("review"),
      improveRanking: at("improve-ranking/:id"),
    })),
    library: at("library", (at) => ({
      feeds: at("feeds", (at) => ({
        new: at("new"),
        detail: at(":id"),
      })),
      catalogue: at("catalogue", (at) => ({
        fields: at("fields"),
        subjects: at("subjects"),
        documents: at("documents"),
        rejected: at("rejected"),
      })),
      coverage: at("coverage", (at) => ({
        gaps: at("gaps", (at) => ({
          detail: at(":id"),
        })),
        uncoveredClaims: at("uncovered-claims"),
      })),
      taxonomy: at("taxonomy", (at) => ({
        gaps: at("gaps"),
      })),
      // The Library's former flat tabs, kept as redirects.
      legacyDocuments: at("documents"),
      legacyRejected: at("rejected"),
      legacyCoverageGaps: at("coverage-gaps"),
      legacyTaxonomyGaps: at("taxonomy-gaps"),
    })),
    // The former home of the Documents page, kept as a redirect.
    legacyDocuments: at("documents"),
    batchJobs: at("batch-jobs", (at) => ({
      detail: at(":id"),
    })),
    investigations: at("investigations", (at) => ({
      claimTrace: at("trace"),
      corpusScan: at("corpus-scan"),
      documentTrace: at("extraction", (at) => ({
        detail: at(":documentId"),
      })),
    })),
    admin: at("admin", (at) => ({
      users: at("users"),
    })),
  })),
}));

/** The sign-in page, returning to `redirect` once signed in. */
export function signInPath(redirect?: string): string {
  return redirect
    ? `${ROUTES.signIn.path()}?redirect=${encodeURIComponent(redirect)}`
    : ROUTES.signIn.path();
}

/** The Library page of one feed. */
export function feedPath(feed: Pick<CorpusFeed, "id">): string {
  return ROUTES.internal.library.feeds.detail.path({ id: feed.id });
}

/** The trace of one document: how its source was resolved, parsed and admitted. */
export function documentTracePath(documentId: string): string {
  return ROUTES.internal.investigations.documentTrace.detail.path({ documentId });
}

/** The feedback form, optionally about one result and noting the page it was opened from. */
export function feedbackPath({
  verification,
  from,
}: { verification?: string; from?: string } = {}): string {
  const qs = new URLSearchParams();
  if (verification) qs.set("verification", verification);
  if (from) qs.set("from", from);
  const query = qs.toString();
  return query ? `${ROUTES.account.feedback.path()}?${query}` : ROUTES.account.feedback.path();
}
